import {
  documentAttachmentEndpoint,
  documentsEndpoint,
} from '../constants/endpoints';
import { CoreState } from '../modules/index';
import {
  APIAttachment,
  APIDocument,
  APIDocumentSearchFilter,
  APIDocumentSearchResult,
  APISearchSortOrder,
  APISortOrder,
  APILanguage
} from '../types/api';
import { httpMethod } from '../utils/sra';

export enum DocumentFields {
  Id = 'id',
  Title = 'title',
  IsCategory = 'isCategory',
  Position = 'position',
  SectionId = 'sectionId',
  ParentId = 'parentId',
  Text = 'text',
  PublishedDate = 'publishedDate',
}

export enum DocumentsSections {
  HelpCenterWebDispatcher = '915b407b-1754-4838-9ed0-c68dd40abf3b',
  HelpCenterWebContractor = '3f3b398d-0aca-4a21-9800-9b47d4be6088',
  HelpCenterMobileContractor = 'd84b8970-10f0-4e2d-abe2-e883cce60894',
  WhatIsNewWebDispatcher = 'd71ce5d4-74ed-45ba-b7f5-76296998fedc',
  WhatIsNewWebContractor = '8d4f73ab-0868-4618-930f-14ae0d0cfef8',
  WhatIsNewMobileContractor = '1ccad3f0-9f11-48dc-add6-9864452c2e90',
  TermsAndConditionsWebDispatcher = 'c17b1a0f-c9df-47a6-9db2-ca9e59d4a4b2',
  TermsAndConditionsWebContractor = 'f48d6434-9f55-449c-a9a5-880f9f42543b',
  TermsAndConditionsMobileContractor = 'aea4f66e-eaa2-438d-9bdf-805ded62095e',
  TermsAndConditionsLanding = '73798957-7eda-45d5-abb8-5329c6b44403'
}

export type CoreDocumentType = {
  id: string;
  name: string | null;
  isCategory: boolean;
  document: APIDocument;
};

export type CoreDocumentTypeItem = CoreDocumentType & {
  children: CoreDocumentType[];
};

export type CoreDocumentsState = {
  items: APIDocumentSearchResult[];
  itemsLoaded: boolean;
  pageNumber: number;
  pageSize: number;
  columns: string[];
  searchSortOrders: APISearchSortOrder<DocumentFields>[];
  uploadedAttachment: APIAttachment | null;
};

export const coreDocumentsDefaultState: CoreDocumentsState = {
  items: [],
  itemsLoaded: false,
  pageNumber: 1,
  pageSize: 1000, // is maximum
  searchSortOrders: [
    {
      sortField: DocumentFields.Position,
      sortOrder: APISortOrder.ASC,
    },
  ],
  columns: [
    'id',
    'isCategory',
    'author',
    'position',
    'publishedDate',
    'title',
    'sectionId',
    'parentId',
    'text',
    'optimizedSearchText',
  ],
  uploadedAttachment: null,
};

export const loadDocuments =
  <State extends CoreState, Payload extends APIDocumentSearchResult>(
    actionName: string,
  ) =>
    (
      providerId: string,
      sectionId: DocumentsSections,
      language: APILanguage,
      getModuleState: (state: any) => CoreDocumentsState,
      body?: Partial<APIDocumentSearchFilter>,
    ) => ({
      name: actionName,
      url: documentsEndpoint(providerId, sectionId),
      body: (state: State) => ({
        pageNumber: getModuleState(state).pageNumber,
        pageSize: getModuleState(state).pageSize,
        columns: getModuleState(state).columns,
        searchSortOrders: getModuleState(state).searchSortOrders, 
        language: language,
        ...body,
      }),
      method: httpMethod.post,
      onSuccess: (_state: State, payload: Payload) => {
        return {
          items: payload,
          itemsLoaded: true,
        };
      },
    });

export const resetDocuments = (actionName: string) => () => ({
  name: actionName,
  updater: () => coreDocumentsDefaultState,
});

export const fetchDocument =
  (actionName: string) =>
    (providerId: string, sectionId: DocumentsSections, documentId: string) => ({
      name: actionName,
      url: documentsEndpoint(providerId, sectionId, documentId),
      method: httpMethod.get,
    });

export const saveDocument =
  <State extends CoreDocumentsState, Payload extends APIDocument>(
    actionName: string,
  ) =>
    (
      providerId: string,
      sectionId: DocumentsSections,
      language: APILanguage,
      body?: Partial<APIDocument>,
    ) => ({
      name: actionName,
      url: documentsEndpoint(providerId, sectionId, body?.[DocumentFields.Id]),
      body: () => ({
        ...body,
        language: language,
      }),
      method: httpMethod.put,
      onSuccess: (state: State, payload: Payload) => ({
        items: body?.[DocumentFields.Id]
          ? state.items.reduce<APIDocumentSearchResult[]>((result, it) => {
            return [
              ...result,
              it.document.id === body?.[DocumentFields.Id]
                ? { ...it, document: { ...it.document, ...payload } }
                : it,
            ];
          }, [])
          : [...state.items, { document: payload }],
      }),
    });

export const deleteDocument =
  <State extends CoreDocumentsState>(actionName: string) =>
    (providerId: string, sectionId: DocumentsSections, documentId: string) => ({
      name: actionName,
      url: documentsEndpoint(providerId, sectionId, documentId),
      method: httpMethod.delete,
      onSuccess: (state: State) => ({
        items: state.items.filter((it) => it.document?.id !== documentId),
      }),
    });

export const uploadDocumentAttachment =
  (actionName: string) =>
    (providerId: string, sectionId: string, formData: FormData) => ({
      name: actionName,
      url: documentAttachmentEndpoint(providerId, sectionId),
      body: formData,
      method: httpMethod.post,
    });
